<template>
  <section id="shopAssembly">
    <div id="breadcrumb">
      <div class="container">
        <ul>
          <li><router-link to="/" tag="a">Home</router-link></li>
          <li>
            <router-link to="/orders/create" tag="a"
              >Create New Order</router-link
            >
          </li>
          <li>
            <router-link to="/orders/chooseassembly" tag="a"
              >Choose Assembly</router-link
            >
          </li>
          <li class="last">Choose Part</li>
        </ul>
      </div>
    </div>

    <b-form id="assemblyForm" @submit.prevent="">
      <div id="assemblyList">
        <div class="container assembly-body-wrap">
          <div class="row">
            <div class="col-12 assembly-results-col">
              <!-- ===== DIAGRAM BANNER =====
                   Click-to-view assembly diagram, sitting at
                   the top of the products column so the
                   diagram trigger is visually attached to the
                   parts list. The actual image opens in a
                   zoom modal (defined just below) — keeps the
                   inline page short while still offering the
                   diagram one click away. -->
              <button type="button"
                      class="diagram-banner"
                      data-toggle="modal"
                      data-target="#imagemodal"
                      title="View assembly diagram"
                      @click="resetImageZoom">
                <span class="diagram-banner-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg"
                       viewBox="0 0 24 24"
                       width="18" height="18"
                       fill="none"
                       stroke="currentColor"
                       stroke-width="2"
                       stroke-linecap="round"
                       stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </span>
                <span class="diagram-banner-label">View Assembly Diagram</span>
                <span class="diagram-banner-cta">Open</span>
              </button>

              <!-- Modal — structured header / scrollable
                   image body / 4-button footer. CSS-transform
                   zoom (no library). zoom resets each time
                   the banner is clicked so the modal always
                   opens at 1× regardless of how the user
                   left it last time. -->
              <div class="modal fade"
                   id="imagemodal"
                   tabindex="-1"
                   role="dialog"
                   aria-labelledby="imagemodalLabel"
                   aria-hidden="true">
                <div class="modal-dialog modal-lg modal-image-zoom">
                  <div class="modal-content">
                    <div class="modal-header image-zoom-header">
                      <h5 class="modal-title" id="imagemodalLabel">
                        {{ machineDescription || 'Assembly Diagram' }}
                      </h5>
                      <button type="button"
                              class="close"
                              data-dismiss="modal"
                              @click="resetImageZoom"
                              aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div class="modal-body image-zoom-body">
                      <div class="image-zoom-canvas">
                        <img :src="image || require('../../../assets/img/jc-steele-placehold.jpg')"
                             alt="Assembly diagram"
                             class="image-zoom-img"
                             :style="{ transform: 'scale(' + imageZoom + ')' }" />
                      </div>
                    </div>
                    <div class="modal-footer image-zoom-footer">
                      <button type="button"
                              class="image-zoom-btn"
                              :disabled="imageZoom <= 0.5"
                              @click="zoomOut"
                              title="Zoom out">−</button>
                      <button type="button"
                              class="image-zoom-btn"
                              :disabled="imageZoom >= 3"
                              @click="zoomIn"
                              title="Zoom in">+</button>
                      <button type="button"
                              class="image-zoom-btn"
                              @click="resetImageZoom"
                              title="Reset zoom">RESET</button>
                      <button type="button"
                              class="image-zoom-btn image-zoom-btn-close"
                              data-dismiss="modal"
                              @click="resetImageZoom">CLOSE</button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty state when the selected assembly has
                   no sub-assemblies. Used to live inside the
                   now-removed "Select Parts to Display in
                   Grid" form; preserved here so users still
                   get a clear message instead of a silently
                   blank list. -->
              <div class="alert alert-danger"
                   v-if="!isSubAssemblyLoading && subAssemblies.length <= 0">
                Sorry, there are no sub-assemblies for the selected assembly at
                this time.
              </div>
              <div class="assembly-card"
                   v-for="assemblies in subAssemblies"
                   :key="assemblies.value"
                   :data-id="assemblies.value">
            <button type="button"
                    class="assembly-card-header"
                    :class="{ 'is-open': isAssemblyExpanded(assemblies.value) }"
                    @click="toggleAssembly(assemblies.value)"
                    :aria-expanded="isAssemblyExpanded(assemblies.value) ? 'true' : 'false'">
              <span class="assembly-card-chev">
                {{ isAssemblyExpanded(assemblies.value) ? '−' : '+' }}
              </span>
              <span class="assembly-card-titles">
                <span class="assembly-card-name">{{ assemblies.text }}</span>
              </span>
              <span class="assembly-card-count">
                {{ assemblies.parts ? assemblies.parts.length : 0 }}
                {{ (assemblies.parts && assemblies.parts.length === 1) ? 'part' : 'parts' }}
              </span>
            </button>

            <div class="assembly-card-body"
                 v-show="isAssemblyExpanded(assemblies.value)">
              <b-table stacked="lg" :fields="fields" :items="assemblies.parts">
                <!-- A virtual column -->
                <template v-slot:cell(item)="data">
                  <div class="nowrap">
                    {{ data.item.item }}
                  </div>

                  <div style="display: none">
                    {{ (data.item.machineId = machineId) }}
                  </div>
                  <div style="display: none">
                    {{ (data.item.assemblyId = assemblyId) }}
                  </div>
                </template>

                <!-- A custom formatted column -->
                <template v-slot:cell(code)="data">
                  <div class="nowrap part-code-cell">
                    <span>{{ data.item.code }}</span>
                    <!-- Inline 2% Discount chip — appears
                         when this part is in the global
                         wear-parts catalog. Uses the same
                         (partId || id) fallback as the rest
                         of the site since the API response
                         shape varies between controllers. -->
                    <span class="wear-discount-chip"
                          v-if="wearPartIds[data.item.partId || data.item.id]">
                      2% Discount
                    </span>
                  </div>
                </template>

                <!-- A virtual composite column -->
                <template v-slot:cell(description)="data">
                  <div class="text-left">
                    {{ data.item.description }}
                  </div>
                </template>

                <!-- Optional default data cell scoped slot -->
                <template v-slot:cell(quantityPerMachine)="data">
                  {{ data.item.quantityPerMachine }}
                </template>

                <template v-slot:cell(price)="data">
                  {{ data.item.price | currency }}
                </template>

                <template v-slot:cell(quantity)="data">
                  <button
                    class="btn btn-dark table-btn-icon table-btn-added"
                    @click="scrollTo()"
                    v-show="data.item.show"
                    aria-label="Already added"
                    title="Added">
                    ✓
                  </button>
                  <div style="display: none">{{ data.item.show }}</div>
                  <input
                    type="number"
                    v-model="data.item.quantity"
                    placeholder="1"
                    class="table-input"
                    v-show="!data.item.show"
                  />
                  <button
                    v-show="!data.item.show"
                    class="btn btn-dark table-btn-icon"
                    @click="addToPartsList(data.item);"
                    aria-label="Add part"
                    title="Add part">
                    +
                  </button>
                </template>

                <template v-slot:cell(cost)="data">
                  <div style="display: none">
                    {{
                      (data.item.cost = data.item.price * data.item.quantity)
                    }}
                  </div>
                  <div v-if="data.item.cost">
                    {{ data.item.cost | currency }}
                  </div>
                  <div v-else>{{ data.item.price | currency }}</div>
                </template>
              </b-table>
            </div>
          </div>
            </div>
          </div>
        </div>
      </div>

      <div id="partsList">
        <div class="container">
          <!-- Single-row action bar — replaces the previous
               4-level-nested col-lg-6 offset-lg-6 grid that
               was creating a tall blank horizontal band of
               whitespace before the button. -->
          <div class="parts-list-actions">
            <button class="btn btn-primary parts-list-actions-btn"
                    @click="goToCart()">
              Go To Cart
            </button>
          </div>
        </div>
      </div>
    </b-form>
  </section>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
const apiService = new FeApiService();

import "expose-loader?$!expose-loader?jQuery!jquery";
import "semantic-ui-dropdown/dropdown.js";

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
  name: "CreateOrderShopAssembly",
  computed: {
    // Heading adapts to the entry point: a cart "Add Parts" click means the
    // machine is already in the cart, so we frame it as adding more parts
    // instead of showing the plain machine name.
    pageTitle() {
      if (this.isFromCart && this.machineName) {
        return "Add more parts to your " + this.machineName;
      }
      return this.machineName || "";
    },

    // ===== UPSELL COMPUTEDS =====
    // Filter the customer's frequently-ordered list to exclude
    // parts already in this session's partsList — once a user
    // adds a part, the suggestion stops appearing in the
    // upsell rail to keep the panel's content fresh.
    filteredUpsellParts() {
      var top = (this.insights && this.insights.topParts) || [];
      if (!top.length) return [];

      var staged = {};
      (this.partsList || []).forEach(function (p) {
        var pid = p && (p.partId || p.id);
        if (pid != null) staged[pid] = true;
      });

      return top.filter(function (p) {
        return !staged[p.partId];
      });
    },

    // Sliced view for "See more" pagination.
    displayedUpsellParts() {
      return this.filteredUpsellParts.slice(0, this.upsellLimit);
    },

    canShowMoreUpsell() {
      return this.filteredUpsellParts.length > this.upsellLimit;
    },

    // ===== STAGING TRAY (cart-derived) =====
    // Flatten fullOrder.purchaseOrders into a per-(part, PO)
    // list. fullOrder is the source of truth — saveCart
    // updates it on every successful add — so this stays in
    // sync with the actual cart automatically. No extra state
    // mirroring required.
    selectedParts() {
      var arr = [];
      var pos = (this.fullOrder && this.fullOrder.purchaseOrders) || [];
      pos.forEach(function (po) {
        (po.parts || []).forEach(function (p) {
          // Field-name tolerance: the part object can come
          // from three sources (locally pushed, saveCart
          // response, hydrated cart) which historically
          // diverged on casing. Try the camelCase name
          // first, fall back to PascalCase, then alternate
          // names (partCode), then any case-insensitive
          // variant. Same applies to description.
          var code =
            p.code ||
            p.Code ||
            p.partCode ||
            p.PartCode ||
            (p.part && (p.part.code || p.part.partCode)) ||
            "";
          var desc =
            p.description ||
            p.Description ||
            p.partDescription ||
            (p.part && p.part.description) ||
            "";
          arr.push({
            partId: p.id || p.partId || p.Id || p.PartId,
            partCode: code,
            description: desc,
            quantity: p.quantity || p.Quantity || p.quantityPerMachine || 1,
            poNumber: po.number || po.Number,
            _po: po,
            _part: p,
          });
        });
      });
      return arr;
    },

    // Group selectedParts by poNumber, in order of first
    // appearance. First PO encountered = Order #1, second =
    // Order #2, etc. Same shape as Create_-_Order so we can
    // use the same insights-* template markup verbatim.
    groupedSelections() {
      var groups = {};
      var poOrder = [];
      (this.selectedParts || []).forEach(function (p) {
        var po = p.poNumber || "(no PO)";
        if (!groups[po]) {
          groups[po] = { poNumber: po, parts: [] };
          poOrder.push(po);
        }
        groups[po].parts.push(p);
      });
      return poOrder.map(function (po, i) {
        return {
          orderNumber: i + 1,
          poNumber: groups[po].poNumber,
          parts: groups[po].parts,
        };
      });
    },

    // Total count across all groups — drives the tray
    // header's "N parts across M orders" label.
    selectedPartsCount() {
      return (this.selectedParts || []).length;
    },
  },
  mounted() {
    if (this.$route.params.fullOrder) {
      this.fullOrder = this.$route.params.fullOrder;
    }

    if (this.$route.params.image) {
      this.image = this.$route.params.image;
    }

    if (this.$route.params.partsList) {
      this.partsList = [];
      this.poNumber = this.$route.params.number;
      localStorage.poNumber = this.poNumber;
      this.machineId = this.$route.params.machineId;
      this.assemblyId = this.$route.params.assemblyId;
      this.cartId = this.$route.params.id;
      this.configuredWear = this.$route.params.configuredWear;
      this.fullOrder = this.$route.params.fullOrder;
      this.order = this.$route.params.order;

      // Cart-driven entry — flip the heading to "Add more parts to your..."
      this.isFromCart = true;

      this.updateSubtotal(this.partsList);
    }

    // this.machineDetails();
    this.getSubAssembly();
    // this.getWearItems();
    this.getAssembly();
    this.loadWearPartIds();
    this.loadUpsellInsights();
  },
  data() {
    return {
      cartId: localStorage.cartId,
      assemblyId: localStorage.assemblyId,
      machineId: localStorage.machineId,
      configuredWear: false,
      poNumber: localStorage.poNumber,
      companyId: localStorage.companyId,
      // True when the user arrived via "Add Parts" from a cart row — the
      // page heading swaps to "Add more parts to your..." to reflect that
      // the machine is already in the cart.
      isFromCart: false,
      order: "",
      machineName: "",
      machineDescription: "",
      editPoNumber: false,
      subTotal: 0,
      subAssemblies: [],
      selectedSubAssemblies: [],
      parts: [],
      partList: [],
      displayParts: [],
      wearParts: [],
      fullOrder: "",
      fields: [
        "item",
        "code",
        "description",
        { key: "quantityPerMachine", label: "QTY/MACH" },
        "price",
        { key: "quantity", label: "Cart Qty" },
        "cost",
      ],
      partItems: [],
      partsFields: [
        "item",
        "code",
        "description",
        { key: "quantityPerMachine", label: "QTY/MACH" },
        "price",
        { key: "quantity", label: "Cart Qty" },
        "cost",
      ],
      partsList: [],
      image: "",
      url: "https://steeleimages.blob.core.windows.net/images/",
      isSubAssemblyLoading:true,
      // Map of PartId → true for parts in the global Wears
      // catalog. Drives the inline "2% Discount" chip next to
      // each part's code in the parts table. Loaded on mount.
      wearPartIds: {},
      // Per-sub-assembly expand state — keyed by
      // assemblies.value (matches the existing :key on the
      // v-for). Default empty so all cards start COLLAPSED.
      // Lets the page render compactly even when a machine
      // has many sub-assemblies; user clicks a header to
      // expand a specific parts table.
      expandedAssemblies: {},
      // ===== FREQUENTLY ORDERED UPSELL =====
      // Mirrors the insights model used on the dashboard,
      // create pages, and Search page. Same getInsights
      // endpoint, same data shape — only `topParts` is
      // rendered here. Defaults to an empty list so the
      // template renders cleanly during loading and on error.
      insights: { topParts: [] },
      // Pagination for the upsell list — start at 5, "See
      // more" bumps by 5 each click.
      upsellLimit: 5,
      // Friendly company display name for the panel subtitle.
      // Resolved from the companies list once that loads.
      companyName: "",
      // True while a sync is in flight; disables the upsell
      // ADD buttons and the staging × buttons to prevent
      // overlapping mutations of the cart.
      addingInsightPart: false,
      // ===== STAGING-TRAY SYNC PLUMBING =====
      // When the user mutates the staging tray (× a row,
      // Clear all, qty edit), we don't fire saveCart on every
      // keystroke. Instead we coalesce changes through a
      // 300ms debounce window so rapid edits collapse into a
      // single round-trip. _syncTimer holds the pending
      // setTimeout id so successive scheduleSyncToCart calls
      // can clear & re-arm it. _syncDirty is set if a fresh
      // change comes in mid-sync; the in-flight save then
      // re-fires once it resolves so we never end up with
      // local state ahead of the cart.
      _syncTimer: null,
      _syncDirty: false,
      // ===== IMAGE ZOOM MODAL =====
      // Current zoom level for the assembly-diagram modal.
      // 1.0 = native size; clamped to [0.5, 3.0] by the
      // zoom buttons. Reset to 1 on every modal open so each
      // click of the banner starts fresh.
      imageZoom: 1,
    };
  },
  methods: {
    // Load the customer's most-ordered parts for the
    // "Frequently Ordered" rail. Same getInsights endpoint
    // the dashboard / create / search pages use. Soft-fail on
    // error — the panel is best-effort, not load-bearing.
    async loadUpsellInsights() {
      var companyId = this.companyId || localStorage.companyId || "";
      if (!companyId) return;
      try {
        var data = await apiService.getInsights({
          companyId: companyId,
          allTime: false, // YTD by default
          take: 50,
        });
        this.insights = data || { topParts: [] };

        // Best-effort: pull a friendly company name for the
        // panel subtitle. Falls back to "your company" via
        // template guard when this hasn't resolved.
        if (apiService.getCompanyList) {
          apiService.getCompanyList().then((list) => {
            var match = (list || []).find((c) => {
              return String(c.value) === String(companyId);
            });
            if (match) this.companyName = match.text;
          }).catch(() => { /* ignore */ });
        }
      } catch (e) {
        console.warn("[ShopAssembly] upsell insights load failed:", e);
        this.insights = { topParts: [] };
      }
    },

    // Quick-add an upsell part: route to /search with the
    // part code pre-filled, so the user lands in the unified
    // search-add-to-cart flow that already knows how to
    // resolve the part's machine + assembly + PO context.
    // We don't try to add directly here because the upsell
    // part may belong to a DIFFERENT assembly than the one
    // the user is currently shopping — Search will surface
    // it correctly with full context.
    openUpsellPart(part) {
      if (!part) return;
      this.$router.push({
        name: "Search",
        query: {
          code: part.partCode,
          allParts: "true",
        },
      });
    },

    // ===== IMAGE ZOOM CONTROLS =====
    // Bound to the modal's −/+/RESET footer buttons.
    // Quarter-step increments give noticeable but not
    // jarring scale changes; clamps at 0.5×–3× so the user
    // can't lose the image off-screen or shrink it to
    // unreadable.
    zoomIn() {
      this.imageZoom = Math.min(3, this.imageZoom + 0.25);
    },
    zoomOut() {
      this.imageZoom = Math.max(0.5, this.imageZoom - 0.25);
    },
    resetImageZoom() {
      this.imageZoom = 1;
    },

    // ===== STAGING-TRAY MUTATIONS =====
    // Remove one (part, PO) pairing from the cart. The full
    // dance of state to keep in sync:
    //   1. fullOrder.purchaseOrders — removed via splice
    //      (with the empty-PO group also pruned)
    //   2. partsList — addToCart's first line WIPES
    //      fullOrder and rebuilds from partsList, so any
    //      part still in partsList will get resurrected on
    //      the next ADD click. Filter by id here so future
    //      ADDs don't bring this part back.
    //   3. subAssemblies[i].parts[j].show — controls the
    //      ✓ vs +/qty toggle in the parts table; flip false
    //      so the row goes back to "+" state.
    //   4. server cart — kicked via debounced sync.
    removeQueuedPart(p) {
      if (!p || !this.fullOrder) return;
      var pos = this.fullOrder.purchaseOrders || [];
      var poIdx = pos.indexOf(p._po);
      if (poIdx < 0) return;
      var po = pos[poIdx];
      var partIdx = (po.parts || []).indexOf(p._part);
      if (partIdx < 0) return;
      po.parts.splice(partIdx, 1);
      // Empty PO → drop it entirely. Avoids "Order #N · 0 parts"
      // dead groups in the tray.
      if (po.parts.length === 0) {
        pos.splice(poIdx, 1);
      }
      // Sync local-side partsList — see method docstring for
      // why this matters even though it feels redundant.
      this.partsList = (this.partsList || []).filter(function (pl) {
        return pl.id !== p.partId;
      });
      // Flip the parts table back to "+" for this part. Without
      // this, the parts table keeps showing ✓ even though the
      // staging tray has dropped it — UI desync that confuses
      // the user.
      this.unmarkAddedInTable(p.partId);
      this.scheduleSyncToCart();
    },

    // Clear all parts from the cart. Same four-state-sync
    // rationale as removeQueuedPart — fullOrder, partsList,
    // table show flags, and server cart all reset together.
    clearSelectedParts() {
      if (!this.fullOrder) return;
      this.fullOrder.purchaseOrders = [];
      // partsList must also be wiped — see removeQueuedPart's
      // explanation. Without this, the next ADD click would
      // resurrect every previously-added part because
      // addToCart rebuilds fullOrder from partsList.
      this.partsList = [];
      // Reset every part's "added" flag in the parts table so
      // every row shows the qty input + "+" again.
      var self = this;
      (this.subAssemblies || []).forEach(function (sa) {
        (sa.parts || []).forEach(function (pt) {
          self.$set(pt, "show", false);
        });
      });
      this.scheduleSyncToCart();
    },

    // Inverse of updateBtn — called when the staging tray
    // removes a part. Walks every sub-assembly's parts array
    // and resets `show=false` on any part matching this
    // partId, so the b-table re-renders the input+plus
    // controls instead of the ✓ "added" icon.
    unmarkAddedInTable(partId) {
      if (partId == null) return;
      var self = this;
      (this.subAssemblies || []).forEach(function (sa) {
        (sa.parts || []).forEach(function (pt) {
          if (pt.id === partId) {
            self.$set(pt, "show", false);
          }
        });
      });
    },

    // Debounce wrapper around syncQueueToCart. Coalesces
    // rapid mutations (qty edits, sequential ×s) into a
    // single round-trip 300ms after the last change.
    scheduleSyncToCart() {
      if (this._syncTimer) {
        clearTimeout(this._syncTimer);
      }
      var self = this;
      this._syncTimer = setTimeout(function () {
        self._syncTimer = null;
        self.syncQueueToCart();
      }, 300);
    },

    // Push the current fullOrder.purchaseOrders state back to
    // the server. If the cart is now empty we deleteCart
    // entirely. If a sync is already in flight we just mark
    // _syncDirty and let the in-flight save re-fire on
    // resolve, so we never end up with local state ahead of
    // the server.
    async syncQueueToCart() {
      if (this.addingInsightPart) {
        this._syncDirty = true;
        return;
      }
      if (!this.cartId || String(this.cartId) === "0") {
        // No backing cart yet — nothing to sync. The first
        // user-initiated add will create one via the existing
        // addToCart flow.
        return;
      }
      this.addingInsightPart = true;
      var self = this;

      try {
        var pos = (this.fullOrder && this.fullOrder.purchaseOrders) || [];

        // Empty cart → deleteCart. Same outcome as the user
        // hitting Clear Cart on the /cart page; reset local
        // state so a subsequent add starts a fresh cart.
        if (pos.length === 0) {
          try {
            await apiService.deleteCart({ id: parseInt(this.cartId, 10) });
          } catch (e) {
            console.warn("[ShopAssembly] deleteCart failed (possibly already gone):", e);
          }
          localStorage.cartId = 0;
          this.cartId = 0;
          this.fullOrder = "";
          // Header counter zero-out.
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        // Non-empty: send the fresh purchaseOrders payload.
        // saveCart's response refreshes fullOrder.purchaseOrders
        // server-authoritatively, so any client-side desync
        // (e.g. qty edits the server clamped) gets reconciled.
        var resp = await apiService.saveCart({
          id: this.cartId || 0,
          number: this.poNumber,
          purchaseOrders: pos,
          machineId: this.machineId,
          assemblyId: this.assemblyId,
          companyId: this.companyId,
        });
        if (resp && resp.purchaseOrders) {
          this.fullOrder.purchaseOrders = resp.purchaseOrders;
        }
        if (resp && resp.id) {
          this.cartId = resp.id;
          localStorage.cartId = resp.id;
        }
        // Header counter refresh after qty edits / × removes.
        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
      } catch (e) {
        console.warn("[ShopAssembly] syncQueueToCart failed:", e);
      } finally {
        this.addingInsightPart = false;
        // If a fresh change came in while we were syncing,
        // re-fire so the user's intent isn't lost.
        if (this._syncDirty) {
          this._syncDirty = false;
          this.scheduleSyncToCart();
        }
      }
    },

    // Pull THIS company's wear-eligible PartIds (its dbo.Wears
    // = the Account Setup wear list) for the inline "2% Discount"
    // chip in the parts table. The chip must reflect the
    // customer's OWN wear parts, not the global wear-eligible
    // catalog — so use the company-scoped endpoint keyed by
    // localStorage.companyId. Defensive against missing API
    // method, missing companyId, bad response shape, network
    // failure (or a pre-deploy 404 on the new endpoint) — all
    // paths fall back to {} (no chips, no crash).
    loadWearPartIds() {
      if (!apiService.getCompanyWearPartIds) {
        console.warn("[ShopAssembly] loadWearPartIds skipped — apiService.getCompanyWearPartIds missing");
        this.wearPartIds = {};
        return;
      }
      var companyId = localStorage.companyId || "";
      if (!companyId) {
        console.warn("[ShopAssembly] loadWearPartIds skipped — no companyId yet");
        this.wearPartIds = {};
        return;
      }
      var self = this;
      apiService
        .getCompanyWearPartIds(companyId)
        .then(function (data) {
          // Tolerate several shapes the backend could
          // return (flat numeric array, array of objects
          // with partId / id / Id / PartId).
          var map = {};
          if (Array.isArray(data)) {
            data.forEach(function (item) {
              if (item == null) return;
              var id;
              if (typeof item === "number" || typeof item === "string") {
                id = item;
              } else if (typeof item === "object") {
                id = item.partId || item.PartId || item.id || item.Id;
              }
              if (id != null) map[id] = true;
            });
          } else {
            console.warn("[ShopAssembly] getCompanyWearPartIds returned non-array:", data);
          }
          self.wearPartIds = map;
          console.log("[ShopAssembly] loaded", Object.keys(map).length, "wear part ids");
        })
        .catch(function (err) {
          console.warn("[ShopAssembly] could not load wear part ids:", err);
          self.wearPartIds = {};
        });
    },

    // Accordion helpers — flip per-sub-assembly expand state
    // so the parts table only renders when the user clicks
    // the card header. Vue.set is required because we're
    // adding new keys to a reactive object that didn't have
    // them at init time.
    isAssemblyExpanded(key) {
      return !!this.expandedAssemblies[key];
    },
    toggleAssembly(key) {
      var current = !!this.expandedAssemblies[key];
      this.$set(this.expandedAssemblies, key, !current);
    },

    fail(err) {
      this.$bvToast.toast(err.error.response.data.traceId ? "TraceID: " + err.error.response.data.traceId : err.error.response.data, {
          title: err.error.response.data.title,
          autoHideDelay: 10000,
          appendToast: false,
          variant: "danger",
          solid: true,
        });
    },
    getSubAssembly() {
      apiService
        .getAssemblyWithParts({
          id: this.assemblyId,
        })
        .then((request) => {
          this.selectItemsSuccessful(request);
        })
        .catch((err) => this.selectItemsFailed(err));
    },
    selectItemsSuccessful(request) {
      this.subAssemblies = request;

      this.subAssemblies.forEach((item) => {
        item.parts.forEach((part) => {
          part.quantity = part.quantityPerMachine;
        });

        this.showAssemblyParts(item.value);
        this.partList.push(item.value);
      });
    },
    selectItemsFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    getAssembly() {
      apiService
        .getAssemblyDetails({
          id: this.assemblyId,
        })
        .then((request) => {
          this.getAssemblySuccessful(request);
        })
        .catch((err) => this.getAssemblyFailed(err));
    },
    getAssemblySuccessful(request) {
      if (request.imagePath != null) {
        this.image = this.url + request.imagePath;
      } else {
        this.image = null;
      }
    },
    saveCartSuccessful(request) {
      localStorage.cartId = request.id;
      // Keep the data field in sync — addToCart pulls from
      // localStorage on each click, but syncQueueToCart and
      // other paths read this.cartId directly. Without this
      // line they could see a stale id after the first save.
      this.cartId = request.id;
      this.fullOrder.purchaseOrders = request.purchaseOrders;
      // Notify the header so the "View Cart" counter
      // refreshes. The Head.vue listener for this event
      // re-fetches the cart count on the fly.
      if (this.$cartBus) {
        this.$cartBus.$emit("cart-updated");
      }
    },
    getAssemblyFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    goToCart() {
      this.$router.push("/cart");
    },
    getParts(id) {
      apiService
        .getPartsByAssembly({
          id: id,
        })
        .then((request) => {
          this.selectPartsItemsSuccessful(request);
        })
        .catch((err) => this.selectItemsFailed(err));
    },
    selectPartsItemsSuccessful(request) {
      this.partItems = request;
    },
    // machineDetails() {
    //   console.log('machine ' + this.companyMachineId);
      
    //   apiService
    //     .getCompanyMachineDetails({ id: this.companyMachineId })
    //     .then((request) => this.machineSuccessful(request))
    //     .catch((err) => this.machineFailed(err));
    // },
    // machineSuccessful(request) {
    //   (this.machineName = request.code),
    //     (this.machineDescription = request.description);
    // },
    // machineFailed(err) {
    //   this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
    //     title: err.error.response.data.title,
    //     autoHideDelay: 10000,
    //     appendToast: false,
    //     variant: "danger",
    //     solid: true,
    //   });
    // },
    changePoNumber(num) {
      if (num) {
        localStorage.poNumber = num;
  
        this.editPoNumber = !this.editPoNumber;  
      } else {
        this.$bvToast.toast("Please enter a valid PO Number to continue", {
          title: "Warning! PO Number field cannot be empty",
          autoHideDelay: 5000,
          appendToast: false,
          variant: "danger",
          solid: true
      });
      }
    },
    showAssemblyParts(assembly) {
      this.selectedSubAssemblies.push(assembly);
      this.isSubAssemblyLoading = false
      //  console.log(this.selectedSubAssemblies);
      $("[data-id='" + assembly + "']").show();
    },
    showParts(assembly) {
      if (event.target.tagName === "INPUT") {
        if (event.target.checked) {
          // Checkbox is being checked
          // the value that will be set is event.target._value

          this.selectedSubAssemblies.push(assembly);
          //  console.log(this.selectedSubAssemblies);
          $("[data-id='" + assembly + "']").show();
        } else {
          // Checkbox is being un-checked
          // the value that will be unset is set is event.target._value
          this.selectedSubAssemblies.splice(
            this.selectedSubAssemblies.indexOf(assembly),
            1
          );
          // console.log(this.selectedSubAssemblies);
          $("[data-id='" + assembly + "']").hide();
        }
      }
    },

    // getWearItems() {
    //   apiService
    //     .getWearItems({
    //       id: this.assemblyId
    //     })
    //     .then(request => {
    //       this.wareItemsSuccessful(request);
    //     })
    //     .catch(err => this.wareItemsFailed(err));
    // },
    // wareItemsSuccessful(request) {
    //   this.wearParts = request;

    //   this.configuredWear = localStorage.configuredWear;
    // },
    // wareItemsFailed(err) {
    //   this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
    //       title: err.error.response.data.title,
    //       autoHideDelay: 10000,
    //       appendToast: false,
    //       variant: "danger",
    //       solid: true
    //   });
    // },

    addToPartsList(part) {
      this.partsList = [];
      part.show = true;
      if (!part.quantity) {
        part.quantity = part.quantityPerMachine;
        part.cost = part.price * part.quantity;
      }

      var idx = $.inArray(part, this.partsList);
      if (idx == -1) {
        this.partsList.push(part);
      } else {
        for (var i in this.partsList) {
          if (this.partsList[i].id == part.id) {
            this.partsList[i].quantity = part.qty;
            break; //Stop this loop, we found it!
          }
        }
      }
      this.$forceUpdate(this.partsList);
      this.addToCart(part);
      this.updateSubtotal(this.partsList);

      // If there are any existing purchaseOrders with the poNumber and matching Machine and Assembly,
      // and the part to the existing list so the list does not get cleared out
      // if (this.fullOrder.purchaseOrders) {
      //     for (var p in this.fullOrder.purchaseOrders) {
      //       if (this.fullOrder.purchaseOrders[p].number == this.poNumber
      //           && part.machineId == this.fullOrder.purchaseOrders[p].machineId
      //           && part.assemblyId == this.fullOrder.purchaseOrders[p].assemblyId) {

      //         this.fullOrder.purchaseOrders[p].parts.push(part);

      //       }
      //     }
      //   }
    },
    scrollTo() {
      $("html, body").animate(
        {
          scrollTop: $("#partsList").offset().top,
        },
        500
      );
    },
    removeItem(item) {
      this.subTotal = this.subTotal - item.cost;

      var idx = $.inArray(item, this.partsList);
      this.partsList.splice(idx, 1);
      item.show = false;
    },
    removeAllParts() {
      for (var i in this.partsList) {
        this.partsList[i].show = false;
      }
      this.partsList = [];
      this.subTotal = 0;
    },
    addToCart(part) {
      this.cartId = localStorage.cartId || this.cartId;
      if (this.fullOrder) {
        this.fullOrder.purchaseOrders = [];
        var po = this.poNumber;
        var assemblyID = this.assemblyId;
        if (
          this.fullOrder.purchaseOrders.filter(function(e) {
            return e.number == po;
          }).length > 0
        ) {
          // console.log(this.partsList);
          if (
            this.fullOrder.purchaseOrders.filter(function(e) {
              return e.assemblyId == assemblyID;
            }).length > 0
          ) {
            // nothing

            for (let i in this.partsList) {
              newPO = {
                assemblyId: this.partsList[i].assemblyId || this.assemblyId,
                machineId: this.machineId,
                parts: [this.partsList[i]],
                Number: this.poNumber,
                companyId: this.companyId,
                cartId: this.cartId,
                configuredWear: this.configuredWear,
              };

              this.fullOrder.purchaseOrders.push(newPO);
            }
          } else {
            var newPO = {
              active: true,
              assemblyId: this.assemblyId,
              machineId: this.machineId,
              number: this.poNumber,
              parts: this.partsList,
              cartId: this.cartId,
              Number: this.poNumber,
              configuredWear: this.configuredWear,
            };

            this.fullOrder.purchaseOrders.push(newPO);
          }
          apiService
            .saveCart({
              id: this.cartId || 0,
              number: this.poNumber,
              purchaseOrders: this.fullOrder.purchaseOrders,
              machineId: this.machineId,
              assemblyId: this.assemblyId,
              companyId: this.companyId,
            })
            .then((request) => {
              this.saveCartSuccessful(request);
              this.updateBtn(part);
            })
            .catch((err) => this.fail(err));
        } else {
          newPO = {
            active: true,
            assemblyId: this.assemblyId,
            machineId: this.machineId,
            number: this.poNumber,
            parts: this.partsList,
            cartId: this.cartId,
            Number: this.poNumber,
            configuredWear: this.configuredWear,
          };

          this.fullOrder.purchaseOrders.push(newPO);

          apiService
            .saveCart({
              id: this.cartId || 0,
              number: this.poNumber,
              purchaseOrders: this.fullOrder.purchaseOrders,
              machineId: this.machineId,
              assemblyId: this.assemblyId,
              companyId: this.companyId,
            })
            .then((request) => {
              this.saveCartSuccessful(request);
              this.updateBtn(part);
            })
            .catch((err) => this.fail(err));
        }
      } else {
        this.fullOrder = {
          purchaseOrders: [],
        };

        for (let i in this.partsList) {
          newPO = {
            assemblyId: this.partsList[i].assemblyId,
            machineId: this.machineId,
            parts: [this.partsList[i]],
            Number: this.poNumber,
            configuredWear: this.configuredWear,
          };

          this.fullOrder.purchaseOrders.push(newPO);
        }
        apiService
            .saveCart({
              id: this.cartId || 0,
              number: this.poNumber,
              purchaseOrders: this.fullOrder.purchaseOrders,
              machineId: this.machineId,
              assemblyId: this.assemblyId,
              companyId: this.companyId,
            })
            .then((request) => {
              this.saveCartSuccessful(request);
              this.updateBtn(part);
            })
            .catch((err) => this.fail(err));
      }
    },
    updateBtn(part) {
      this.subAssemblies.forEach(machine => {
      const partToUpdate = machine.parts.find(p => p.id === part.id);
      if (partToUpdate) {
        this.$set(partToUpdate, 'show', true);
      }
    });
    },
    updateSubtotal(list) {
      this.subTotal = 0;

      for (var i in list) {
        this.subTotal = this.subTotal + list[i].cost;
      }
    },
  },
};
</script>

<style scoped>
/* ===== PAGE HEADING + PO BADGE ===== */
.page-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 8px;
}

.page-heading-row h1 {
  margin: 0;
  flex: 1 1 auto;
  min-width: 240px;
}

.page-heading-po {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
  padding: 8px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.po-badge-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #777;
}

.po-badge-value {
  font-size: 15px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

@media (max-width: 767.98px) {
  .page-heading-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .page-heading-po {
    width: 100%;
    justify-content: space-between;
  }
}

/* ===== ASSEMBLY ACCORDION CARDS =====
   Replaces the previous always-expanded list of sub-assembly
   sections. Each sub-assembly is now a single card that
   starts collapsed; the parts table only renders when the
   user clicks the header. Visual treatment matches the rest
   of the site: white surface, brand-red top accent, soft
   shadow, generous padding. */
.assembly-card {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
  overflow: hidden;
}

.assembly-card-header {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 14px 18px;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease;
}

.assembly-card-header:hover {
  background: #faf5f4;
}

.assembly-card-header:focus {
  outline: none;
  background: #faf5f4;
  box-shadow: inset 0 0 0 2px rgba(192, 57, 43, 0.15);
}

.assembly-card-header.is-open {
  border-bottom: 1px solid #ececec;
}

.assembly-card-chev {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  font-family: monospace;
}

.assembly-card-titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.assembly-card-name {
  font-size: 15px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
}

.assembly-card-machine {
  font-size: 12px;
  color: #888;
  font-weight: 500;
}

.assembly-card-count {
  font-size: 12px;
  color: #555;
  font-weight: 600;
  background: #f3f3f3;
  padding: 4px 10px;
  border-radius: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.assembly-card-body {
  padding: 8px 18px 16px 18px;
}

.assembly-card-body .b-table,
.assembly-card-body .table {
  margin-bottom: 0;
}

/* ===== WEAR DISCOUNT CHIP (in-table) ===== */
.part-code-cell {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.wear-discount-chip {
  display: inline-block;
  padding: 1px 6px;
  background: #e8f5e9;
  border: 1px solid #2e7d32;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #2e7d32;
  white-space: nowrap;
  line-height: 1.4;
}

/* ===== TWO-COLUMN BODY LAYOUT =====
   Left column hosts the accordion of sub-assemblies. Right
   column hosts the staging tray (what the user has added
   this session) and the Frequently Ordered upsell panel.
   Same overall layout as the Search page so the three flows
   (Search / ShopAssembly / ShopWearParts) feel coherent. */
.assembly-body-wrap {
  margin-top: 12px;
}

/* Top-of-page Frequently Ordered cell — sits to the right
   of the assembly image. Keeps the panel snug against the
   top of the container so the image and panel start on the
   same baseline. */
.assembly-top-right-col {
  display: flex;
  flex-direction: column;
}

.assembly-top-right-col > .upsell-panel {
  /* Take the full available height of the column so the
     panel feels intentional even when the parts list below
     is short. */
  flex: 1 1 auto;
}

/* ===== DIAGRAM BANNER =====
   Full-width clickable bar at the top of the parts column
   (col-lg-8). Acts as a header for the products container —
   per the user's request, the diagram trigger is "right in
   the top bar of the container for the products" rather
   than off in its own column. Click opens #imagemodal. */
.diagram-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  margin: 0 0 14px 0;
  background: #fff;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
  color: #c0392b;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.diagram-banner:hover,
.diagram-banner:focus {
  background: #fff5f3;
  border-color: #c0392b;
  outline: none;
}

.diagram-banner-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: #c0392b;
  color: #fff;
  border-radius: 4px;
}

.diagram-banner-label {
  flex: 1 1 auto;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-size: 12px;
  color: #2b2b2b;
}

.diagram-banner-cta {
  flex-shrink: 0;
  font-size: 11px;
  color: #c0392b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ===== ASSEMBLY DIAGRAM MODAL =====
   Header with title (machineDescription) on top, scrollable
   image canvas in the middle, full-width 4-button footer
   (-, +, RESET, CLOSE) at the bottom. Image zooms via CSS
   transform — modal-body scrolls when the scaled image
   exceeds the viewport, so the user can pan around the
   zoomed diagram naturally with the scrollbar/touchpad. */
.modal-image-zoom .modal-content {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18);
}

.image-zoom-header {
  padding: 12px 18px;
  border-bottom: 1px solid #ececec;
  background: #fff;
}

.image-zoom-header .modal-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #2b2b2b;
  margin: 0;
}

.image-zoom-body {
  padding: 0;
  background: #fafafa;
  overflow: auto;
  /* Cap the modal body height so the buttons stay visible
     even when the image is zoomed. 65vh leaves room for the
     site chrome on smaller laptops. */
  max-height: 65vh;
}

.image-zoom-canvas {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  padding: 16px;
}

.image-zoom-img {
  display: block;
  max-width: 100%;
  height: auto;
  /* Smooth scale transition so zooms feel intentional
     instead of snappy. */
  transition: transform 0.18s ease;
  transform-origin: center center;
}

.image-zoom-footer {
  display: flex;
  padding: 0;
  margin: 0;
  border-top: 1px solid #ececec;
  background: #fff;
}

.image-zoom-btn {
  flex: 1 1 0;
  padding: 14px 12px;
  background: #fff;
  border: none;
  border-right: 1px solid #ececec;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #2b2b2b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.image-zoom-btn:hover:not(:disabled) {
  background: #f5f5f5;
}

.image-zoom-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.image-zoom-btn:last-child {
  border-right: none;
}

/* CLOSE — brand-red highlight, white text. Visually
   distinguishes the dismiss action from the zoom controls. */
.image-zoom-btn-close {
  background: #c0392b;
  color: #fff;
}

.image-zoom-btn-close:hover {
  background: #a93222;
  color: #fff;
}

/* ===== GO TO CART ACTION BAR =====
   Compact right-aligned action area below the products row.
   Replaces the previous nested-grid form-btns layout that
   was creating a tall blank horizontal whitespace band
   before the button. */
.parts-list-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 0 4px;
}

.parts-list-actions-btn {
  min-width: 160px;
}

/* ===== TIGHT PARTS TABLE =====
   Tuned to fit all 7 columns (item, code, description,
   qty/mach, price, cart qty, cost) inside col-lg-8 without
   horizontal overflow. Compact font + minimal padding gives
   the description column room to breathe. */
.assembly-card-body {
  padding: 6px 12px 12px 12px;
}

.assembly-card-body .table {
  font-size: 11.5px;
  table-layout: auto;
}

.assembly-card-body .table th,
.assembly-card-body .table td {
  padding: 6px 5px;
  font-size: 11.5px;
  vertical-align: middle;
}

.assembly-card-body .table th {
  font-size: 9.5px;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

/* Item / qty / price / cost columns — narrow numeric. Set
   width: 1px so the browser collapses them to their content
   width and gives all the slack to description.
   NOTE: column 6 (CART QTY) is intentionally excluded —
   that column needs real width for input + button. */
.assembly-card-body .table th:nth-child(1),
.assembly-card-body .table td:nth-child(1),
.assembly-card-body .table th:nth-child(4),
.assembly-card-body .table td:nth-child(4),
.assembly-card-body .table th:nth-child(5),
.assembly-card-body .table td:nth-child(5),
.assembly-card-body .table th:nth-child(7),
.assembly-card-body .table td:nth-child(7) {
  width: 1px;
  white-space: nowrap;
}

/* Cart Qty column — input + + button must stay on one
   line. Without nowrap and inline-block, the input was
   getting full width and the + dropped below it (the
   stacked-vertical bug shown in the user's screenshot).
   min-width holds the column wide enough to fit both. */
.assembly-card-body .table th:nth-child(6),
.assembly-card-body .table td:nth-child(6) {
  white-space: nowrap;
  min-width: 86px;
  text-align: center;
}

/* Cart Qty input — compact width, inline-block so it sits
   next to the + button instead of stacking above it. */
.assembly-card-body .table .table-input {
  display: inline-block;
  vertical-align: middle;
  width: 38px;
  padding: 3px 4px;
  font-size: 11px;
  text-align: center;
  margin: 0;
}

/* ===== ICON BUTTONS (Add / Added) =====
   Square 26×26 buttons replacing the old "Add" / "Added"
   text buttons. + for available, ✓ for already-added. The
   ✓ variant uses a slightly different shade so the two
   states are distinguishable at a glance. */
.assembly-card-body .table .table-btn-icon {
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  margin-left: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  border-radius: 4px;
}

.assembly-card-body .table .table-btn-icon.table-btn-added {
  background: #777;
  border-color: #777;
  color: #fff;
  cursor: default;
}

.assembly-card-body .table .part-code-cell {
  font-size: 11.5px;
}

.assembly-results-col {
  /* Default — no special positioning. Lets the accordion
     scroll with the page. */
}

.assembly-side-col {
  /* Stack staging vertically with consistent spacing. */
}

.assembly-side-col > .insights-selected-tray,
.assembly-side-col > .upsell-panel {
  margin-bottom: 16px;
}

.assembly-side-col > :last-child {
  margin-bottom: 0;
}

@media (min-width: 992px) {
  .assembly-side-col {
    /* Sticky rail on desktop so the staging summary stays
       visible as the user scrolls long parts tables on the
       left. */
    position: sticky;
    top: 16px;
    align-self: flex-start;
  }
}

/* ===== STAGING TRAY (cart-derived, grouped by PO) =====
   Same visual treatment as the Create_-_Order page's
   selected-parts tray so users get a coherent staging UX
   across the create / shop flows. Cream/yellow tones
   distinguish the tray from the rest of the page (hard to
   miss but not aggressively branded). */
.insights-selected-tray {
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #fffaf0;
  border: 1px solid #f3e3b8;
  border-radius: 6px;
}

.insights-selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.insights-selected-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #8a6d2a;
}

.insights-selected-clear {
  background: transparent;
  border: none;
  padding: 0;
  font-size: 11px;
  color: #888;
  text-decoration: underline;
  cursor: pointer;
}

.insights-selected-clear:hover {
  color: #c0392b;
}

/* Each PO group is its own card-within-card. Slightly
   lighter background than the tray so the grouping reads
   as a visual hierarchy without needing borders everywhere. */
.insights-order-group {
  margin-top: 10px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #ecdfb3;
  border-radius: 4px;
}

.insights-order-group:first-of-type {
  margin-top: 4px;
}

.insights-order-group-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
  padding-bottom: 5px;
  border-bottom: 1px dashed #f0e3b5;
}

.insights-order-group-num {
  font-size: 12px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.insights-order-group-meta {
  font-size: 11px;
  color: #8a6d2a;
}

.insights-order-group-meta strong {
  color: #5a4716;
}

/* Compact mini-cart row inside a group: code | qty input | × */
.insights-queue-rows {
  list-style: none;
  margin: 0;
  padding: 0;
}

.insights-queue-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f5e9c5;
}

.insights-queue-row:last-child {
  border-bottom: none;
}

/* Wraps code + (optional) description so the two stack
   vertically and the rest of the row (qty, ×) stays
   horizontal next to them. */
.insights-queue-row-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.insights-queue-row-code {
  font-size: 12px;
  font-weight: 700;
  color: #5a4716;
  letter-spacing: 0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insights-queue-row-desc {
  font-size: 10px;
  color: #8a6d2a;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insights-queue-row-qty {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.insights-qty-input {
  width: 56px;
  padding: 3px 6px;
  font-size: 12px;
  font-weight: 600;
  color: #222;
  text-align: center;
  border: 1px solid #d4c486;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.insights-qty-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

.insights-qty-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a6d2a;
}

.insights-queue-row-remove {
  flex-shrink: 0;
  background: transparent;
  border: none;
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 16px;
  font-weight: 700;
  color: #888;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.insights-queue-row-remove:hover {
  background: #c0392b;
  color: #fff;
}

.insights-queue-row-remove:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== FREQUENTLY ORDERED UPSELL PANEL ===== */
.upsell-panel {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.upsell-header {
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #d8d8d8;
}

.upsell-title {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.upsell-sub {
  margin: 0;
  font-size: 12px;
  color: #777;
  line-height: 1.4;
}

.upsell-sub strong {
  color: #444;
}

.upsell-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.upsell-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.upsell-item:last-child {
  border-bottom: none;
}

.upsell-item-main {
  flex: 1 1 auto;
  min-width: 0;
}

.upsell-item-code {
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
  margin-bottom: 2px;
}

.upsell-item-desc {
  font-size: 13px;
  color: #333;
  line-height: 1.35;
  margin-bottom: 4px;
}

.upsell-item-meta {
  font-size: 11px;
  color: #888;
  font-style: italic;
}

.upsell-item-meta strong {
  color: #555;
  font-style: normal;
}

.upsell-item-add {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #c0392b;
  background: #fff;
  border: 1.5px solid #c0392b;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.upsell-item-add:hover {
  background: #c0392b;
  color: #fff;
}

.upsell-item-add:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.upsell-see-more {
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px dashed #e5e5e5;
  text-align: center;
}

.upsell-see-more-btn {
  background: transparent;
  border: 1px solid #c0392b;
  color: #c0392b;
  padding: 5px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-radius: 4px;
  cursor: pointer;
}

.upsell-see-more-btn:hover {
  background: #c0392b;
  color: #fff;
}
</style>